import { EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import { isSearchResultMatchKeyWords } from "../../utils/searchResultMatchKeywords/searchResultMatchKeywords";
import { ApiManager } from "../api";
import { DataSearchResult } from "../dataManager/IDataApi";
import { MailSync } from "../mail/MailSync";
import { ACTIVE_SMART_LABELS, Mail, SmartLabel } from "../mail/types/mail";
import { effectiveSmartLabel } from "../mail/utils/effectiveSmartLabel";
import type { FocusSortMode } from "../user/types/settings";
import { sortMailEntries, toValidDate } from "./customViewMailEntry";
import { CustomViewState } from "./CustomViewState";
import { compareFocusEntries } from "./focusMailOrdering";
import {
    sanitizeExclusiveSmartLabelEntries,
    toIdArray,
} from "./smartLabelEntry";
import { SmartLabelStore } from "./SmartLabelStore";
import {
    CustomView,
    CustomViewMailEntry,
    CustomViewPatchParams,
    CustomViewSortOrder,
    CustomViewType,
    SmartLabelEntry,
    ViewFilterMailStatus,
} from "./types/customViews";

/** Marque de retrait : le mail est supprimé, il n'y a aucun état à enfiler. */
const PENDING_REMOVAL = Symbol("custom-view-pending-removal");

type PendingMailEntry = Mail | typeof PENDING_REMOVAL;

export class CustomViewMembership {
    private eventBus: EventBus;

    private apiManager: ApiManager;

    private mailSync: MailSync;

    private viewState: CustomViewState;

    private smartLabelStore: SmartLabelStore;

    /** Mode de tri du Focus choisi par l'utilisateur, lu dans son profil à chaque appel. */
    private getFocusSortMode: () => FocusSortMode;

    private readonly DEBOUNCE_DELAY = 2000; // 2 secondes

    /** Mails en attente d'écriture, par vue : dernier état connu de chaque mail. Une seule file
     *  porte les ajouts et les retraits, donc aucune course entre les deux pour un même mail. */
    private _pendingMailsByViewId = new Map<
        string,
        Map<string, PendingMailEntry>
    >();

    /** Écriture programmée et pas encore démarrée : tout mail enfilé avant son départ y sera emporté. */
    private _scheduledFlushByViewId = new Map<string, Promise<boolean>>();

    /** Écriture de membership en cours, par vue (cf. `_withViewLock`). */
    private _viewWriteLocks = new Map<string, Promise<unknown>>();

    /** Fenêtre de regroupement en attente, par vue — `settle` la clôt sans attendre (cleanup). */
    private _flushWindowByViewId = new Map<
        string,
        { timeout: ReturnType<typeof setTimeout>; settle: () => void }
    >();

    /** Nombre maximum de mails dans une CustomView.
     *  Décision PO du 21/07/2026 (« 2000 max dans un premier temps »).
     *  Les compteurs documents/liens/événements/todos reflètent donc les mails présents dans la vue. */
    private readonly CUSTOM_VIEW_MAX_MAILS = 2000;

    private lastExecutionTimes: Map<string, number> = new Map<string, number>();

    /** Throttle par label pour la vérification de suppression des mails déjà en Record (cf. _emitVerifyExistingMailsForView).
     *  Filet de sécurité de fond : la détection passe désormais par le backend (2 round-trips Graph + retry témoin),
     *  donc inutile de la relancer à chaque from=0. Une fois par minute par label suffit largement. */
    private _lastMailVerificationAt = new Map<SmartLabel, number>();

    private readonly _MAIL_VERIFICATION_DEBOUNCE_MS = 60000;

    constructor(
        eventBus: EventBus,
        apiManager: ApiManager,
        mailSync: MailSync,
        viewState: CustomViewState,
        smartLabelStore: SmartLabelStore,
        getFocusSortMode: () => FocusSortMode,
    ) {
        this.eventBus = eventBus;
        this.apiManager = apiManager;
        this.mailSync = mailSync;
        this.viewState = viewState;
        this.smartLabelStore = smartLabelStore;
        this.getFocusSortMode = getFocusSortMode;
    }

    /**
     * Charge les SmartLabels dans le mode de tri courant.
     * @returns `null` si le mode a changé pendant l'appel (bascule) : la réponse est périmée et ne doit pas
     * remplacer le store.
     */
    public async fetchSmartLabelsInCurrentFocusSortMode(): Promise<Awaited<
        ReturnType<ApiManager["getSmartLabelsForUser"]>
    > | null> {
        const requestedFocusSortMode = this.getFocusSortMode();
        const smartLabels = await this.apiManager.getSmartLabelsForUser(
            requestedFocusSortMode,
        );

        if (this.getFocusSortMode() !== requestedFocusSortMode) {
            logger.info(
                [LOGS.CustomViewManager],
                `SmartLabels ignorés : demandés en ${requestedFocusSortMode}, mode passé à ${this.getFocusSortMode()} pendant l'appel`,
            );
            return null;
        }
        return smartLabels;
    }

    /**
     * Recharge le seul Focus dans le mode courant, pour la bascule du mode de tri. Remplace le store sans
     * condition : à la bascule, les mêmes mails changent seulement d'ordre, ce que le rafraîchissement
     * habituel (comparaison d'ensembles d'identifiants) ignorerait.
     * Les autres labels restent ceux du store : un mail déplacé pendant l'appel est recalé au prochain
     * rafraîchissement complet (même limite que `_backgroundRefreshSmartLabels`).
     * @throws si l'appel échoue : la bascule doit le savoir ; le store reste alors intact.
     */
    public async reloadFocus(): Promise<void> {
        const requestedFocusSortMode = this.getFocusSortMode();
        const focus = await this.apiManager.getSmartLabel(
            SmartLabel.Focus,
            requestedFocusSortMode,
        );

        // Une bascule plus récente a changé le mode pendant l'appel : c'est son rechargement qui remplit le store
        if (this.getFocusSortMode() !== requestedFocusSortMode) {
            logger.info(
                [LOGS.CustomViewManager],
                `Rechargement du Focus ignoré : demandé en ${requestedFocusSortMode}, mode passé à ${this.getFocusSortMode()} pendant l'appel`,
            );
            return;
        }

        // [#1672 — fix ① exclusivité] Focus prioritaire : un mail rechargé dans le Focus quitte les autres labels
        const sanitized = sanitizeExclusiveSmartLabelEntries({
            ...this.smartLabelStore.entries,
            [SmartLabel.Focus]: focus.entries,
        });
        this.smartLabelStore.focusSortMode = focus.focusSortMode;

        for (const label of ACTIVE_SMART_LABELS) {
            const currentEntries = this.smartLabelStore.entries[label] ?? [];
            const reloadedEntries = sanitized[label] ?? [];
            // Le Focus est toujours remplacé (ordre) ; un autre label seulement s'il a perdu un mail
            if (
                label === SmartLabel.Focus ||
                reloadedEntries.length !== currentEntries.length
            ) {
                this.smartLabelStore.entries[label] = reloadedEntries;
                this.eventBus.emit("custom-view-list-updated", {
                    customViewId: label,
                });
            }
        }
        this.eventBus.emit("navigation-asking-for-update");

        logger.info(
            [LOGS.CustomViewManager],
            `Focus rechargé en ${focus.focusSortMode} (${focus.entries.length} mails)`,
        );
    }

    public _cleanup(): void {
        this.lastExecutionTimes = new Map();
        // Clôt les fenêtres en attente : le flush qui suit ne trouve ni mail ni vue → no-op.
        // `_scheduledFlushByViewId` n'est pas vidé : chaque chaîne s'en retire en démarrant.
        this._pendingMailsByViewId.clear();
        this._viewWriteLocks.clear();
        this._flushWindowByViewId.forEach(({ timeout, settle }) => {
            clearTimeout(timeout);
            settle();
        });
        this._flushWindowByViewId.clear();
        this._lastMailVerificationAt.clear();
    }

    public async getView(customViewId: string): Promise<CustomView> {
        logger.info(LOGS.CustomViewManager, `getView(${customViewId})`);

        try {
            // Check dans le Record
            const recordView = this.viewState.viewsRecordById[customViewId];

            if (recordView) {
                return recordView;
            }

            // Si pas trouvé dans le Record, chercher dans le dbManager

            const dbView: CustomView =
                await this.apiManager.getCustomView(customViewId);
            logger.info(LOGS.CustomViewManager, `View from DB`, dbView);
            return dbView;
        } catch (error) {
            logger.error(
                LOGS.CustomViewManager,
                "Error retrieving custom view",
                error,
            );
            throw error;
        }
    }

    /**
     * Met à jour le store smartLabelEntries après l'analyse ou la modification d'un mail,
     * et émet 'navigation-asking-for-update' si une modification a eu lieu.
     * Retire le mail de tous les labels (gère les changements de label), puis l'insère
     * dans le label effectif à la bonne position triée :
     *   - Focus  : règle miroir du tri backend (`focusMailOrdering.ts`), dans le mode du store
     *     (`smartLabelStore.focusSortMode`)
     *   - Autres : sentDateTime DESC
     *
     * Règle isUserProcessed : si mail.isUserProcessed === true, le mail est toujours
     * redirigé vers Traités, quel que soit mail.smartLabel.
     */
    public _updateSmartLabelEntryAndSendEvent(mail: Mail): void {
        // Regle isUserProcessed : prioritaire sur le smartLabel d'analyse
        // (source unique : effectiveSmartLabel)
        const label = effectiveSmartLabel(mail);
        const hasValidLabel =
            !!label && ACTIVE_SMART_LABELS.includes(label as SmartLabel);

        // Entrée store « candidate » : ce mail tel qu'il serait représenté dans le store
        // (clés de tri lues sur l'objet Mail). Comparée à l'entrée existante pour décider du placement.
        const sentDate = mail.sentDateTime
            ? new Date(mail.sentDateTime)
            : undefined;
        const candidateEntry: SmartLabelEntry = {
            id: mail.internetMessageId,
            date: sentDate,
            score: mail.score ?? undefined,
            manualFocusDate: mail.manualFocusDate ?? null,
            sentDateTimeMailvista: mail.sentDateTimeMailvista ?? null,
        };

        // Labels où le mail est actuellement présent dans le store.
        const presentLabels = ACTIVE_SMART_LABELS.filter((smartLabel) =>
            (this.smartLabelStore.entries[smartLabel] ?? []).some(
                (entry) => entry.id === mail.internetMessageId,
            ),
        );

        // ─── RC-1/RC-2 : court-circuit « rien de pertinent n'a changé pour les SmartLabels » ───
        // Si le mail est déjà présent UNIQUEMENT dans son label cible ET que ses clés de tri sont
        // identiques à l'entrée store existante (vérité backend), retirer/réinsérer redonnerait un
        // tableau identique. On ne mute rien et on N'ÉMET PAS d'event : un changement hors clés de
        // tri (ex. isRead à la sélection) ne doit pas déclencher de refetch liste ni la cascade
        // verify/éviction. Les CustomViews (qui filtrent sur isRead) restent traitées par
        // _updateAllCustomViewsIfNeededAndSendEvent (appelée séparément par le handler).
        if (
            hasValidLabel &&
            presentLabels.length === 1 &&
            presentLabels[0] === label
        ) {
            const targetEntries =
                this.smartLabelStore.entries[label as SmartLabel] ?? [];
            const existingIdx = targetEntries.findIndex(
                (entry) => entry.id === mail.internetMessageId,
            );
            if (
                existingIdx !== -1 &&
                this._isSameSmartLabelSortKeys(
                    targetEntries[existingIdx],
                    candidateEntry,
                )
            ) {
                // [#1672 — revue PR #204, garde de race] Contenu/tri inchangés → on ne réinsère pas et on
                // n'émet AUCUN event (comportement RC-1/RC-2 conservé : pas de refetch/cascade sur un simple
                // changement hors clés de tri, ex. isRead). MAIS on RAFRAÎCHIT la RÉFÉRENCE de l'entrée :
                // recevoir un `mail-updated` pour ce mail est une PREUVE qu'il existe. Le garde
                // `_removeMessageFromSmartLabelStoreIfUnchanged` raisonne sur l'IDENTITÉ d'objet ; sans ce
                // rafraîchissement, une ré-affirmation via ce court-circuit garderait la même référence, et un
                // verdict Graph périmé (« absent », eventually-consistent) retirerait à tort ce mail vivant
                // pendant le trou async d'une réconciliation → dérive du compteur Focus. Nouvel objet, mêmes
                // données → la ré-affirmation devient détectable et le retrait périmé est ignoré.
                targetEntries[existingIdx] = { ...targetEntries[existingIdx] };
                return;
            }
        }

        // Step 1 — Always remove from every store first, regardless of whether the mail
        // has a new target label. Without this, a mail whose label is cleared or set to
        // an unknown value would remain in its old store (e.g. Focus) indefinitely.
        const removedFromLabels: SmartLabel[] = [];
        for (const activeLabel of ACTIVE_SMART_LABELS) {
            const entries = this.smartLabelStore.entries[activeLabel];
            if (entries) {
                const filtered = entries.filter(
                    (e) => e.id !== mail.internetMessageId,
                );
                if (filtered.length !== entries.length) {
                    this.smartLabelStore.entries[activeLabel] = filtered;
                    removedFromLabels.push(activeLabel);
                }
            }
        }

        // Step 2 — If no valid target label, just notify for any store that changed and stop.
        if (!hasValidLabel) {
            for (const sourceLabel of removedFromLabels) {
                this.eventBus.emit("custom-view-list-updated", {
                    customViewId: sourceLabel,
                });
            }
            if (removedFromLabels.length > 0) {
                this.eventBus.emit("navigation-asking-for-update");
            }
            return;
        }

        // Step 3 — Insert at the correct sorted position in the target store.
        // « candidate… » = le mail qu'on (ré)insère ; « entry… » = l'entrée déjà dans la liste comparée.
        // Comparateur STRICT (<), pas de swap de voisins (RC-3) : en Focus le départage par identifiant
        // (comme l'API) ne laisse jamais deux clés égales ; ailleurs, le mail réinséré passe après ses pairs.
        const entries = this.smartLabelStore.entries[label as SmartLabel] ?? [];
        let insertionIndex: number;

        if (label === SmartLabel.Focus) {
            // Règle miroir de l'API, dans le mode avec lequel le store a été trié : le mail se place avant
            // le premier mail qu'il précède.
            insertionIndex = entries.findIndex(
                (entry) =>
                    compareFocusEntries(
                        candidateEntry,
                        entry,
                        this.smartLabelStore.focusSortMode,
                    ) < 0,
            );
        } else {
            // Autres labels : tri sentDateTime DESC.
            const candidateSentTimestamp = sentDate?.getTime() ?? 0;
            insertionIndex = entries.findIndex(
                (entry) =>
                    (entry.date?.getTime() ?? 0) < candidateSentTimestamp,
            );
        }

        if (insertionIndex === -1) {
            entries.push(candidateEntry);
        } else {
            entries.splice(insertionIndex, 0, candidateEntry);
        }

        this.smartLabelStore.entries[label as SmartLabel] = entries;

        // Notifie la liste du label CIBLE (où le mail vit désormais).
        this.eventBus.emit("custom-view-list-updated", {
            customViewId: label as SmartLabel,
        });
        // Notifie AUSSI chaque label SOURCE que le mail vient de quitter (miroir du Step 2).
        // Sans ça, une vue affichant un label source (ex. Focus après un unfocus vers PourInfo)
        // ne reçoit jamais d'event pour SA liste : le front ne refetch que si customViewId === listId,
        // donc le mail déplacé resterait affiché dans sa liste d'origine.
        for (const sourceLabel of removedFromLabels) {
            if (sourceLabel !== label) {
                this.eventBus.emit("custom-view-list-updated", {
                    customViewId: sourceLabel,
                });
            }
        }
        this.eventBus.emit("navigation-asking-for-update");
    }

    /**
     * Vrai si les deux entrées ont des clés de tri SmartLabel identiques
     * (date, score, manualFocusDate, sentDateTimeMailvista). Sert au court-circuit RC-1/RC-2 :
     * un mail déjà présent dont aucune clé de tri n'a changé ne doit pas être retiré/réinséré.
     */
    private _isSameSmartLabelSortKeys(
        existingEntry: SmartLabelEntry,
        candidateEntry: SmartLabelEntry,
    ): boolean {
        return (
            (existingEntry.date?.getTime() ?? null) ===
                (candidateEntry.date?.getTime() ?? null) &&
            (existingEntry.score ?? null) === (candidateEntry.score ?? null) &&
            (existingEntry.manualFocusDate ?? null) ===
                (candidateEntry.manualFocusDate ?? null) &&
            (existingEntry.sentDateTimeMailvista ?? null) ===
                (candidateEntry.sentDateTimeMailvista ?? null)
        );
    }

    public _getSmartLabelInternetMessageIds(
        label: SmartLabel,
        from: number = 0,
        top?: number,
    ): string[] {
        const allIds = toIdArray(this.smartLabelStore.entries[label] ?? []);
        return top !== undefined
            ? allIds.slice(from, from + top)
            : allIds.slice(from);
    }

    public async _getInternetMessageIdsFromCustomView(
        customViewId: string,
        from: number = 0,
        top: number | undefined = undefined,
    ): Promise<string[]> {
        logger.info(
            [LOGS.CustomViewManager],
            `_getInternetMessageIdsFromCustomView(${customViewId} from=${from}, top=${top})`,
        );

        // Routage SmartLabel : si customViewId est une valeur de l'enum SmartLabel, utiliser le store interne
        if (ACTIVE_SMART_LABELS.includes(customViewId as SmartLabel)) {
            const label = customViewId as SmartLabel;

            if (from === 0) {
                await this._backgroundRefreshSmartLabels();
            }

            const allIds = toIdArray(this.smartLabelStore.entries[label] ?? []);
            const paginated =
                top !== undefined
                    ? allIds.slice(from, from + top)
                    : allIds.slice(from);

            // Vérification provider-side (background) des mails déjà en Record pour cette vue.
            // Couvre la désynchronisation backend (mail supprimé côté Graph mais encore listé dans le SmartLabel).
            if (from === 0) {
                this._emitVerifyExistingMailsForView(label, allIds);
            }

            return paginated;
        }

        let paginatedViewFilterInternetMessageIds: string[] = [];

        // Vérifier si le cache existe déjà pour cette customView
        const cachedResults =
            this._getInternetMessageIdsFromCustomViewCachedResults(
                customViewId,
            );

        // Si des résultats existent en cache, on les utilise
        if (cachedResults) {
            // Appliquer la pagination sur les résultats mis en cache
            paginatedViewFilterInternetMessageIds = top
                ? cachedResults.slice(from, from + top)
                : cachedResults.slice(from);

            // Si on demande la première page, on lance un update en tache de fond.
            if (from === 0) {
                logger.info(
                    LOGS.CustomViewManager,
                    "Fetching first page of custom view => launching membership refresh (lightweight)",
                );
                void this._refreshViewIds(customViewId).catch((error) =>
                    logger.error(
                        LOGS.CustomViewManager,
                        `_refreshViewIds(${customViewId}) en tâche de fond : ${error}`,
                    ),
                );
            }
        }
        // Sinon on fetch pour la première fois, on met à jour Record + Db et on renvoie les données demandées
        else {
            logger.info(
                [LOGS.CustomViewManager],
                `  => No cache found for customView ${customViewId}, performing full search`,
            );

            logger.warn(
                LOGS.CustomViewManager,
                "Should never happen ! If this heappens, investigate why and update this portion of code",
            );

            // Ajout de la customView dans le Record
            const customView: CustomView = await this.getView(customViewId);
            this.viewState.viewsRecordById[customViewId] = customView;

            // Refresh de la customView pour fournir les résultats
            await this._refreshViewIds(customViewId);
            const allViewFilterInternetMessageIds =
                this._getInternetMessageIdsFromCustomViewCachedResults(
                    customViewId,
                ) ?? [];

            // Slice de la pagination demandée par rapport aux nouveaux internetMessageIds reçus
            paginatedViewFilterInternetMessageIds = top
                ? allViewFilterInternetMessageIds.slice(from, from + top)
                : allViewFilterInternetMessageIds.slice(from);
        }

        logger.info(
            [LOGS.CustomViewManager],
            `  returning ${paginatedViewFilterInternetMessageIds.length} results for view`,
            paginatedViewFilterInternetMessageIds,
        );
        return paginatedViewFilterInternetMessageIds;
    }

    /**
     * Récupère les internetMessageIds mis en cache pour une customView
     * @param customViewId - ID de la customView
     * @returns Array des internetMessageIds ou null si pas en cache
     */
    private _getInternetMessageIdsFromCustomViewCachedResults(
        customViewId: string,
    ): string[] | null {
        if (!customViewId || !this.viewState.viewsRecordById[customViewId]) {
            return null;
        }

        const cachedResults =
            this.viewState.viewsRecordById[customViewId].internetMessageIds;
        logger.info(
            [LOGS.CustomViewManager],
            `Retrieved cached results for customView ${customViewId}`,
            cachedResults,
        );
        return cachedResults;
    }

    /**
     * Reconstruit la composition à partir de celle que la vue détient déjà, complétée par les
     * dates fournies. Le Record des mails n'est PAS consulté : il ne contient que les mails
     * chargés, il ne peut pas dater une vue entière.
     */
    private _toMailEntries(
        customViewId: string,
        internetMessageIds: string[],
        knownSentDateTimes?: ReadonlyMap<string, Date | null>,
    ): CustomViewMailEntry[] {
        const current = new Map(
            (this.viewState.viewsRecordById[customViewId]?.mails ?? []).map(
                (mail) => [mail.internetMessageId, mail.sentDateTime],
            ),
        );

        return internetMessageIds.map((internetMessageId) => ({
            internetMessageId,
            sentDateTime:
                toValidDate(knownSentDateTimes?.get(internetMessageId)) ??
                toValidDate(current.get(internetMessageId)),
        }));
    }

    /** Égalité de composition SENSIBLE à l'ordre ET à la date : une date manquante en base
     *  déclenche donc la mise à jour qui la renseigne (reprise des vues existantes). */
    private _areMailEntriesEqual(
        a: readonly CustomViewMailEntry[],
        b: readonly CustomViewMailEntry[],
    ): boolean {
        if (a.length !== b.length) return false;

        return a.every((entry, index) => {
            const other = b[index];
            return (
                entry.internetMessageId === other.internetMessageId &&
                (entry.sentDateTime?.getTime() ?? null) ===
                    (other.sentDateTime?.getTime() ?? null)
            );
        });
    }

    private async _overrideIfNeededCustomViewWithNewIdsInRecordAndDb(
        customViewId: string,
        freshInternetMessageIds: string[],
        knownSentDateTimes?: ReadonlyMap<string, Date | null>,
    ): Promise<boolean> {
        logger.info(
            LOGS.CustomViewManager,
            `_overrideIfNeededCustomViewWithNewIdsInRecordAndDb for CustomView (${customViewId}) with new list of ${freshInternetMessageIds.length} mails`,
        );

        // Check params
        if (!this.viewState.viewsRecordById[customViewId]) {
            const error = `Error during CustomViewUpdate : customView (${customViewId} not found in Record !`;
            logger.error(LOGS.CustomViewManager, error);
            throw new Error(error);
        }

        // Update Record
        const customView = this.viewState.viewsRecordById[customViewId];

        const freshMails = this._toMailEntries(
            customViewId,
            freshInternetMessageIds,
            knownSentDateTimes,
        );

        if (!this._areMailEntriesEqual(customView.mails ?? [], freshMails)) {
            logger.info(
                LOGS.CustomViewManager,
                "Overriding customView with new ids",
            );

            customView.internetMessageIds = freshInternetMessageIds;
            customView.mails = freshMails;

            // Update DB with new internetMessageIds
            try {
                const customViewPatchParams: CustomViewPatchParams = {
                    mails: freshMails,
                };
                logger.info(
                    LOGS.CustomViewManager,
                    `_overrideIfNeededCustomViewWithNewIdsInRecordAndDb for CustomView (${customViewId}) with new list of ${freshInternetMessageIds.length} mails`,
                );

                await this.apiManager.patchCustomView(
                    customViewId,
                    customViewPatchParams,
                );
            } catch (error) {
                logger.error(
                    LOGS.CustomViewManager,
                    "Error during CustomViewUpdate",
                    error,
                );
                throw error;
            }

            // Emit 'custom-view-list-updated'
            this.eventBus.emit("custom-view-list-updated", {
                customViewId:
                    customView.viewType !== CustomViewType.Custom
                        ? customView.name
                        : customViewId,
            });

            return true;
        }

        logger.info(
            LOGS.CustomViewManager,
            "Skipping Override customView with new ids because new list is the same",
        );

        return false;
    }

    /**
     * Refreshes the smartLabelEntries store from the API.
     * Awaité par la navigation pour que les IDs servis (et la vérification provider-side
     * qui suit) reflètent la dernière vue backend disponible.
     * Emits 'custom-view-list-updated' + 'navigation-asking-for-update' uniquement si changement.
     * Fails silently — the in-memory store stays intact on error.
     */
    private async _backgroundRefreshSmartLabels(): Promise<void> {
        logger.info([LOGS.CustomViewManager], `_backgroundRefreshSmartLabels`);

        try {
            const smartLabels =
                await this.fetchSmartLabelsInCurrentFocusSortMode();
            if (!smartLabels) return;

            // [#1672 — fix ① exclusivité] Assainir le double-assignement backend AVANT de comparer/assigner
            // (P2/INV-4 : au plus un label par id) — sinon un id renvoyé dans 2 labels serait recopié verbatim
            // dans le store → double-compte.
            const freshEntries = sanitizeExclusiveSmartLabelEntries(
                smartLabels.entriesByLabel,
            );
            const hasFocusSortModeChanged =
                smartLabels.focusSortMode !==
                this.smartLabelStore.focusSortMode;
            let changedLabels = 0;

            for (const label of ACTIVE_SMART_LABELS) {
                const freshIds = toIdArray(freshEntries[label] ?? []);
                const currentIds = toIdArray(
                    this.smartLabelStore.entries[label] ?? [],
                );
                // Focus trié dans un autre mode : mêmes mails, autre ordre → remplacé quand même
                const isFocusReordered =
                    label === SmartLabel.Focus && hasFocusSortModeChanged;

                if (
                    isFocusReordered ||
                    !this._doArraysHaveSameElements(freshIds, currentIds)
                ) {
                    this.smartLabelStore.entries[label] =
                        freshEntries[label] ?? [];
                    if (label === SmartLabel.Focus) {
                        this.smartLabelStore.focusSortMode =
                            smartLabels.focusSortMode;
                    }
                    this.eventBus.emit("custom-view-list-updated", {
                        customViewId: label,
                    });
                    this.eventBus.emit("navigation-asking-for-update");
                    changedLabels++;
                }
            }

            logger.info(
                [LOGS.CustomViewManager],
                `_backgroundRefreshSmartLabels - done (${changedLabels} label(s) changed)`,
            );
        } catch (error) {
            // Fail silently — store en mémoire intact, aucun impact UX
            logger.warn(
                [LOGS.CustomViewManager],
                `_backgroundRefreshSmartLabels failed: ${error}`,
            );
        }
    }

    /**
     * Émet (debounced) une demande de vérification provider-side des mails fournis.
     * MailManager écoute cet event et appelle getMailsParentFolderIds pour évincer
     * du Record les mails se trouvant dans deletedItems ou introuvables côté provider.
     *
     * Couvre la désynchronisation backend : un mail supprimé côté Graph dont
     * le backend n'a pas encore été informé reste listé dans les SmartLabels mais
     * doit disparaître de la vue.
     */
    private _emitVerifyExistingMailsForView(
        label: SmartLabel,
        internetMessageIds: string[],
    ): void {
        if (internetMessageIds.length === 0) {
            return;
        }

        const now = Date.now();
        const lastAt = this._lastMailVerificationAt.get(label) ?? 0;
        if (now - lastAt < this._MAIL_VERIFICATION_DEBOUNCE_MS) {
            logger.info(
                [LOGS.CustomViewManager],
                `_emitVerifyExistingMailsForView(${label}) - debounced (last call ${now - lastAt}ms ago)`,
            );
            return;
        }
        this._lastMailVerificationAt.set(label, now);

        logger.info(
            [LOGS.CustomViewManager],
            `_emitVerifyExistingMailsForView(${label}) - emitting for ${internetMessageIds.length} ids`,
        );
        this.eventBus.emit("verify-existing-mails-for-view", {
            internetMessageIds,
        });
    }

    /**
     * [AB#1727 — Mode LIGHTWEIGHT] Rafraîchit la LISTE d'ids (membership) d'une CustomView **sans**
     * télécharger le contenu complet des mails.
     *
     * - Recalcule `freshIds` via le filtre de la vue. Ils sont **déjà triés `sentDateTime` DESC** par la
     *   recherche Graph (`$orderby`), donc persistés tels quels (aucun re-fetch/re-tri nécessaire).
     * - Persiste en Record + DB via `_overrideIfNeededCustomViewWithNewIdsInRecordAndDb`.
     * - Émet `navigation-asking-for-update` si la membership a changé.
     *
     * Les objets `Mail` complets pour l'affichage sont fournis par la **pagination**
     * (`MailManager.getCustomViewMails`, 16 par 16) ; le contenu docs/liens/events par le **store léger**
     * `mailViewContentRecordByInternetMessageId`. Ce refresh n'a donc **jamais** besoin de tout fetcher.
     *
     * ⚠️ `CustomViewType.Custom` uniquement. Les SmartLabels/Focus passent par
     * `_backgroundRefreshSmartLabels` (route store dédiée dans `_getInternetMessageIdsFromCustomView`)
     * et ne doivent jamais atteindre ce chemin.
     *
     * Remplace l'ancien `_refreshViewContent` (Mode FULL) qui appelait
     * `fetchMissingMailsIfAnyByInternetMessageIds(freshIds)` juste pour trier — coûteux et surtout, couplé
     * au `slice(0, limit || 100)` de Graph, amputait la membership à 100 (bug AB#1727).
     *
     * Ne PAS émettre 'custom-view-list-updated' quand la liste est inchangée : le front refetch
     * la page 0 sur cet événement, et tout fetch de page 0 rappelle ce refresh
     * (_getInternetMessageIdsFromCustomView) → boucle infinie de requêtes Graph $batch.
     *
     * @param viewId - Id de la CustomView à rafraîchir
     * @returns A Promise that resolves to void
     */
    public async _refreshViewIds(viewId: string): Promise<void> {
        logger.info(
            LOGS.CustomViewManager,
            `_refreshViewIds(${viewId}) [lightweight — membership only, no mail content fetch]`,
        );

        const viewToUpdate: CustomView = this.viewState.viewsRecordById[viewId];

        // Check input params
        if (!viewToUpdate) {
            const errorString = `Error refreshing view ids (${viewId}) : no corresponding view found ! `;
            logger.error([LOGS.CustomViewManager], errorString);
            throw new Error(errorString);
        }

        // Garde-fou : les SmartLabels/Focus ont une route dédiée (_backgroundRefreshSmartLabels).
        // Ils ne doivent jamais passer par ce refresh de membership Custom.
        if (viewToUpdate.viewType !== CustomViewType.Custom) {
            logger.warn(
                [LOGS.CustomViewManager],
                `_refreshViewIds(${viewId}) - non-Custom viewType (${viewToUpdate.viewType}) ignoré ; les SmartLabels utilisent _backgroundRefreshSmartLabels`,
            );
            return;
        }

        // Recalcul de la membership.
        // NE JAMAIS passer de `limit` à getViewFilterInternetMessageIds : un `limit` désactive la
        // pagination @odata.nextLink côté Graph (`while (nextLink && !limit)`) et ne ramènerait qu'UNE page.
        const freshIdsStart = performance.now();
        // La recherche connaît déjà la date — elle s'en sert pour son propre tri.
        const allFreshEntries = await this.mailSync.getViewFilterMailEntries(
            viewToUpdate.filters,
        );
        // Départage à date égale sur l'identifiant, MÊME clé que l'API : sans elle, la liste
        // stockée et la liste recalculée diffèrent à chaque session et la vue est réécrite pour rien.
        const allFreshIds = sortMailEntries(
            allFreshEntries,
            viewToUpdate.filters?.sortOrder ?? CustomViewSortOrder.DESC,
        ).map((entry) => entry.internetMessageId);
        logger.perf(
            LOGS.CustomViewManager,
            `[timing] _refreshViewIds(${viewId}) - getViewFilterMailEntries → ${allFreshIds.length} freshIds`,
            freshIdsStart,
        );

        const freshSentDateTimes = new Map<string, Date | null>(
            allFreshEntries.map((entry) => [
                entry.internetMessageId,
                entry.sentDateTime,
            ]),
        );

        // Borne à CUSTOM_VIEW_MAX_MAILS : « garder les plus récents », indépendamment du sens
        // d'affichage de la vue — tronquer la tête d'une vue ASC jetterait au contraire les plus
        // récents. Même règle que `capCustomViewMails` côté API.
        const keptIds = new Set(
            sortMailEntries(allFreshEntries, CustomViewSortOrder.DESC)
                .slice(0, this.CUSTOM_VIEW_MAX_MAILS)
                .map((entry) => entry.internetMessageId),
        );
        const freshIds = allFreshIds.filter((id) => keptIds.has(id));

        if (allFreshIds.length > freshIds.length) {
            logger.warn(
                [LOGS.CustomViewManager],
                `_refreshViewIds(${viewId}) : ${allFreshIds.length} → ${freshIds.length} mails (borne CustomView appliquée)`,
            );
        }
        // La requête Graph reste HORS verrou : la tenir bloquerait les écritures d'analyse. Seules
        // la comparaison et l'écriture sont exclusives, sur la liste relue à ce moment-là.
        const needToUpdate = await this._withViewLock(viewId, async () => {
            const viewToPersist = this.viewState.viewsRecordById[viewId];
            if (viewToPersist === undefined) {
                return false;
            }

            const before = viewToPersist.mails ?? [];

            // La décision d'écrire appartient au persisteur, qui compare l'identifiant ET la date.
            await this._overrideIfNeededCustomViewWithNewIdsInRecordAndDb(
                viewId,
                freshIds,
                freshSentDateTimes,
            );

            return before !== this.viewState.viewsRecordById[viewId]?.mails;
        });

        if (needToUpdate) {
            // Notifie la navigation (compteurs / liste).
            this.eventBus.emit("navigation-asking-for-update");
        }

        return;
    }

    public async _updateAllCustomViewsIfNeededAndSendEvent(
        updatedMail: Mail,
    ): Promise<void> {
        logger.info(
            [LOGS.CustomViewManager],
            `_updateAllCustomViewsIfNeededAndSendEvent for modified email "${updatedMail.subject}" `,
        );

        // Parallelize updates for all CustomViews
        const updateResults = await Promise.allSettled(
            Object.values(this.viewState.viewsRecordById).map(
                async (customView: CustomView) => {
                    // Check customView and its id not null
                    if (!customView?.id) {
                        const errorString = `_updateAllCustomViewsIfNeededAndSendEvent <${customView?.name}> - CustomView or Id from Record is undefined - Should not happen`;
                        logger.error([LOGS.CustomViewManager], errorString);
                        throw new Error(errorString);
                    }

                    return await this._updateSingleCustomViewIfNeeded(
                        customView,
                        updatedMail,
                    );
                },
            ),
        );

        // Log any errors that occurred
        updateResults.forEach((result, index) => {
            if (result.status === "rejected") {
                const customView = Object.values(
                    this.viewState.viewsRecordById,
                )[index];
                logger.error(
                    [LOGS.CustomViewManager],
                    `Failed to update CustomView ${customView?.name || "unknown"}: ${result.reason}`,
                );
            }
        });

        // Check if any CustomView was updated (only from successful results)
        const hasAnyCustomViewChanged = updateResults.some(
            (result) => result.status === "fulfilled" && result.value === true,
        );

        // Emit event to update navigation
        if (hasAnyCustomViewChanged) {
            this.eventBus.emit("navigation-asking-for-update");
        }
    }

    /**
     * Enfile le mail mis à jour pour cette CustomView, puis programme l'écriture regroupée.
     *
     * L'enfilage précède tout point de suspension : des appelants concurrents ne peuvent pas se
     * perdre mutuellement. La promesse se résout quand l'écriture qui emporte ce mail est terminée.
     *
     * @param customView  - CustomView to check
     * @param updatedMail  - Updated mail to check
     * @returns True if the CustomView was updated
     */
    private _updateSingleCustomViewIfNeeded(
        customView: CustomView,
        updatedMail: Mail,
    ): Promise<boolean> {
        const viewId = customView.id!;

        const pendingMails =
            this._pendingMailsByViewId.get(viewId) ??
            new Map<string, PendingMailEntry>();
        // Le dernier état connu décide — SAUF une marque de retrait, qui l'emporte : sinon une
        // analyse encore en vol pour un mail qu'on vient de supprimer l'efface, et le mail
        // supprimé reste dans la vue. Perdre une mise à jour ne coûte rien, le recalcul la rattrape.
        if (
            pendingMails.get(updatedMail.internetMessageId) !== PENDING_REMOVAL
        ) {
            pendingMails.set(updatedMail.internetMessageId, updatedMail);
        }
        this._pendingMailsByViewId.set(viewId, pendingMails);

        return this._scheduleCustomViewFlush(viewId);
    }

    /**
     * Programme l'écriture regroupée d'une vue, ou rend celle déjà programmée.
     * Une seule écriture par vue est en vol à la fois, et les écritures successives sont espacées
     * d'au moins `DEBOUNCE_DELAY`.
     */
    private _scheduleCustomViewFlush(viewId: string): Promise<boolean> {
        const scheduledFlush = this._scheduledFlushByViewId.get(viewId);
        if (scheduledFlush) {
            return scheduledFlush;
        }

        const flush = (async () => {
            await this._awaitFlushWindow(viewId);

            // Le créneau est libéré AVANT d'attendre le verrou : un mail enfilé à partir d'ici
            // programme l'écriture suivante au lieu d'être emporté par celle-ci, donc jamais perdu.
            this._scheduledFlushByViewId.delete(viewId);
            this.lastExecutionTimes.set(viewId, Date.now());

            try {
                return await this._withViewLock(viewId, () =>
                    this._flushPendingMailsForView(viewId),
                );
            } finally {
                this.lastExecutionTimes.set(viewId, Date.now());
            }
        })();

        this._scheduledFlushByViewId.set(viewId, flush);
        return flush;
    }

    /**
     * Sérialise une séquence lire-modifier-écrire de la membership d'une vue.
     *
     * Trois chemins écrivent `internetMessageIds` et lisent la liste avant un `await` : sans
     * exclusion, le dernier à écrire écrase les autres. Le verrou est par vue.
     */
    private async _withViewLock<T>(
        viewId: string,
        operation: () => Promise<T>,
    ): Promise<T> {
        const previousWrite = this._viewWriteLocks.get(viewId);

        // Chaîné avant tout point de suspension : des appelants concurrents s'ordonnent ici même.
        const currentWrite = (async () => {
            await previousWrite;
            return operation();
        })();

        // L'échec d'une écriture ne doit bloquer ni propager vers la suivante.
        this._viewWriteLocks.set(
            viewId,
            currentWrite.catch(() => undefined),
        );

        return currentWrite;
    }

    /** Attend le créneau d'écriture de la vue : immédiat si aucune écriture récente. */
    private async _awaitFlushWindow(viewId: string): Promise<void> {
        const lastExecution = this.lastExecutionTimes.get(viewId);
        const remainingDelay =
            lastExecution === undefined
                ? 0
                : Math.max(
                      0,
                      this.DEBOUNCE_DELAY - (Date.now() - lastExecution),
                  );

        await new Promise<void>((resolve) => {
            const timeout = setTimeout(() => {
                this._flushWindowByViewId.delete(viewId);
                resolve();
            }, remainingDelay);
            this._flushWindowByViewId.set(viewId, { timeout, settle: resolve });
        });
    }

    /**
     * Applique en UNE écriture l'effet net des mails en attente pour la vue.
     *
     * @returns True if the CustomView membership changed
     */
    private async _flushPendingMailsForView(viewId: string): Promise<boolean> {
        const pendingMails = this._pendingMailsByViewId.get(viewId);
        this._pendingMailsByViewId.delete(viewId);

        const customView = this.viewState.viewsRecordById[viewId];
        if (!pendingMails?.size || !customView) {
            return false;
        }

        try {
            return await this._applyPendingMailsToView(
                viewId,
                customView,
                pendingMails,
            );
        } catch (error) {
            this._restorePendingMails(viewId, pendingMails);
            throw error;
        }
    }

    /**
     * Rend à la file les mails d'un flush qui a échoué, sans quoi un seul échec réseau perdrait
     * toute la rafale agrégée. Aucun flush n'est reprogrammé : le prochain `mail-updated` emporte
     * la file. Un état plus récent déjà enfilé a priorité sur celui qu'on rend.
     */
    private _restorePendingMails(
        viewId: string,
        pendingMails: Map<string, PendingMailEntry>,
    ): void {
        const currentPendingMails = this._pendingMailsByViewId.get(viewId);
        if (currentPendingMails === undefined) {
            this._pendingMailsByViewId.set(viewId, pendingMails);
            return;
        }

        pendingMails.forEach((pendingEntry, internetMessageId) => {
            // Un retrait rendu à la file l'emporte sur ce qui s'y est enfilé pendant l'échec,
            // pour la même raison qu'à l'enfilage : un retrait perdu laisse un mail supprimé
            // visible jusqu'au prochain recalcul de la vue.
            if (
                pendingEntry === PENDING_REMOVAL ||
                !currentPendingMails.has(internetMessageId)
            ) {
                currentPendingMails.set(internetMessageId, pendingEntry);
            }
        });
    }

    /** Effet net des mails en attente sur la membership de la vue, en UNE écriture. */
    private async _applyPendingMailsToView(
        viewId: string,
        customView: CustomView,
        pendingMails: Map<string, PendingMailEntry>,
    ): Promise<boolean> {
        logger.info(
            [LOGS.CustomViewManager],
            `_flushPendingMailsForView(${customView.name}) - ${pendingMails.size} mail(s) en attente`,
        );

        const nextInternetMessageIds = [...customView.internetMessageIds];
        const pendingMailsByInternetMessageId = new Map<string, Mail>();
        let hasInsertion = false;
        let hasRemoval = false;

        for (const [internetMessageId, pendingEntry] of pendingMails) {
            const indexInView =
                nextInternetMessageIds.indexOf(internetMessageId);

            // Un retrait seul n'a aucun ordre à recalculer.
            if (pendingEntry === PENDING_REMOVAL) {
                if (indexInView !== -1) {
                    nextInternetMessageIds.splice(indexInView, 1);
                    hasRemoval = true;
                }
                continue;
            }

            const pendingMail = pendingEntry;
            const isMailMatchCustomView = this._isMailMatchCustomView(
                pendingMail,
                customView,
            );

            if (isMailMatchCustomView && indexInView === -1) {
                nextInternetMessageIds.push(pendingMail.internetMessageId);
                pendingMailsByInternetMessageId.set(
                    pendingMail.internetMessageId,
                    pendingMail,
                );
                hasInsertion = true;
            } else if (!isMailMatchCustomView && indexInView !== -1) {
                nextInternetMessageIds.splice(indexInView, 1);
                hasRemoval = true;
            } else if (
                customView.name === CustomViewType.Focus &&
                isMailMatchCustomView &&
                indexInView !== -1
            ) {
                pendingMailsByInternetMessageId.set(
                    pendingMail.internetMessageId,
                    pendingMail,
                );
                hasInsertion = true;
            }
        }

        if (!hasInsertion && !hasRemoval) {
            return false;
        }

        // Un retrait seul conserve l'ordre existant : inutile de refetcher et retrier la vue.
        const orderedInternetMessageIds = hasInsertion
            ? await this._sortAndCapMembership(
                  customView,
                  nextInternetMessageIds,
                  pendingMailsByInternetMessageId,
              )
            : nextInternetMessageIds;

        // Le tri passe par le reseau : un cleanup a pu vider le Record entre-temps. Ecrire alors
        // leverait, alors qu'une deconnexion pendant une ecriture est un chemin legitime.
        if (this.viewState.viewsRecordById[viewId] === undefined) {
            return false;
        }

        // Sans transmettre la date, un mail que l'analyse vient d'ajouter serait persisté sans
        // elle et rangé en fin de vue.
        const pendingSentDateTimes = new Map<string, Date | null>(
            [...pendingMailsByInternetMessageId.values()].map((mail) => [
                mail.internetMessageId,
                toValidDate(mail.sentDateTime),
            ]),
        );

        return this._overrideIfNeededCustomViewWithNewIdsInRecordAndDb(
            viewId,
            orderedInternetMessageIds,
            pendingSentDateTimes,
        );
    }

    /**
     * Trie la membership (ranking pour Focus, date DESC sinon) et applique la borne de la vue.
     * Les mails déjà en main évitent un aller-retour Graph pour ceux qui viennent d'arriver.
     */
    private async _sortAndCapMembership(
        customView: CustomView,
        internetMessageIds: string[],
        pendingMailsByInternetMessageId: Map<string, Mail>,
    ): Promise<string[]> {
        // Potentiellement LONG !!!
        await this.mailSync.fetchMissingMailsIfAnyByInternetMessageIds(
            internetMessageIds.filter(
                (internetMessageId) =>
                    !pendingMailsByInternetMessageId.has(internetMessageId),
            ),
            `CustomViewManager._sortAndCapMembership(${customView.name})`,
        );

        const orderedMailList: Mail[] = [];
        internetMessageIds.forEach((internetMessageId) => {
            if (!internetMessageId) {
                logger.error(
                    LOGS.CustomViewManager,
                    `    _sortAndCapMembership - null internetMessageId found in View ${customView.name} - ${customView.id}`,
                );
                return;
            }

            const mail =
                this.mailSync._getMailFromRecordById(internetMessageId) ??
                pendingMailsByInternetMessageId.get(internetMessageId);

            if (!mail) {
                logger.error(
                    LOGS.CustomViewManager,
                    `    _sortAndCapMembership - _getMailFromRecordById return null for mail`,
                );
                return;
            }

            orderedMailList.push(mail);
        });

        // Order list by date desc, except for Focus by _ranking desc
        let newOrderedIdsList: string[] =
            customView.name === CustomViewType.Focus
                ? this.mailSync
                      ._sortMailsByRanking(orderedMailList)
                      .map((mail) => mail.internetMessageId)
                : this.mailSync
                      ._sortMailsBySentDateTime(
                          orderedMailList,
                          CustomViewSortOrder.DESC,
                      )
                      .map((mail) => mail.internetMessageId);

        // Sans ce cap la liste dériverait au-delà de la borne mail par mail, à chaque `mail-updated`.
        // La liste vient d'être triée DESC (date, ou ranking pour Focus) donc `slice(0, …)` conserve bien les plus récents.
        if (
            customView.viewType === CustomViewType.Custom &&
            newOrderedIdsList.length > this.CUSTOM_VIEW_MAX_MAILS
        ) {
            logger.warn(
                [LOGS.CustomViewManager],
                `_sortAndCapMembership (${customView.name}) - membership tronquée à la borne : ${newOrderedIdsList.length} → ${this.CUSTOM_VIEW_MAX_MAILS} mails (les plus récents conservés)`,
            );
            newOrderedIdsList = newOrderedIdsList.slice(
                0,
                this.CUSTOM_VIEW_MAX_MAILS,
            );
        }

        return newOrderedIdsList;
    }

    /**
     * Checks for every CustomView if the mails delete imply a change ( remove )
     *
     * Retire un mail (par son internetMessageId) des CustomViews utilisateur qui le contiennent,
     * de façon CONDITIONNELLE (WS5c-2, §2.4 règle 3) : on ne retire que si **aucune copie survivante**
     * du message ne matche encore le filtre de la vue.
     *
     * @param internetMessageId - internetMessageId du mail supprimé
     * @param survivingCopies   - copies du message encore présentes dans le Record (post-suppression)
     */
    public async _removeInEveryCustomViews(
        internetMessageId: string,
        survivingCopies: Mail[],
    ): Promise<void> {
        logger.info(
            [LOGS.CustomViewManager],
            `_removeInEveryCustomViews for deleted mail ${internetMessageId}`,
        );

        // Parallelize removals for all CustomViews
        const removeResults = await Promise.allSettled(
            Object.values(this.viewState.viewsRecordById).map(
                async (customView: CustomView) => {
                    // Check customView and its id not null
                    if (!customView?.id) {
                        const errorString = `_removeInEveryCustomViews - CustomView or Id from Record is undefined - Should not happen`;
                        logger.error([LOGS.CustomViewManager], errorString);
                        throw new Error(errorString);
                    }

                    await this._removeInSingleCustomViewIfNeeded(
                        customView,
                        internetMessageId,
                        survivingCopies,
                    );
                },
            ),
        );

        // Log any errors that occurred
        removeResults.forEach((result, index) => {
            if (result.status === "rejected") {
                const customView = Object.values(
                    this.viewState.viewsRecordById,
                )[index];
                logger.error(
                    [LOGS.CustomViewManager],
                    `Failed to remove mail from CustomView ${customView?.name || "unknown"}: ${result.reason}`,
                );
            }
        });
    }

    /**
     * Checks for a single CustomView if the delete mail imply a change ( remove )
     *
     * WS5c-2 (§2.4 règle 3) : retrait CONDITIONNEL — on ne retire le mail de la vue que si **aucune
     * copie survivante** du message ne matche encore son filtre. Ex. : supprimer la copie inbox d'un
     * self-sent alors que la copie sent matche toujours le filtre → on garde le mail dans la vue.
     *
     * @param customView      - CustomView to check
     * @param internetMessageId - internetMessageId du mail à retirer si plus aucune copie ne matche
     * @param survivingCopies   - copies du message encore présentes dans le Record (post-suppression)
     */
    private async _removeInSingleCustomViewIfNeeded(
        customView: CustomView,
        internetMessageId: string,
        survivingCopies: Mail[],
    ): Promise<void> {
        // Sous verrou dès l'appartenance : une écriture d'analyse en cours peut réinsérer le mail
        // entre ce test et l'enfilage, auquel cas le mail supprimé resterait dans la vue.
        const scheduled = await this._withViewLock(
            customView.id!,
            async (): Promise<{ flush: Promise<boolean> } | null> => {
                const viewToUpdate =
                    this.viewState.viewsRecordById[customView.id!];
                if (viewToUpdate === undefined) {
                    return null;
                }

                // Une copie survivante matche-t-elle encore le filtre ? Si oui, on conserve le mail dans la vue.
                const aSurvivingCopyStillMatches = survivingCopies.some(
                    (copy) => this._isMailMatchCustomView(copy, viewToUpdate),
                );
                if (aSurvivingCopyStillMatches) {
                    logger.info(
                        [LOGS.CustomViewManager],
                        `_removeInSingleCustomViewIfNeeded - ${internetMessageId} conservé dans CustomView(${viewToUpdate.name}) (copie survivante matche le filtre)`,
                    );
                    return null;
                }

                const viewId = viewToUpdate.id!;
                const pendingMails = this._pendingMailsByViewId.get(viewId);

                // Ni dans la vue, ni en attente d'y entrer : rien à retirer, et programmer une
                // écriture pour ça la ferait partir à vide.
                if (
                    !pendingMails?.has(internetMessageId) &&
                    !viewToUpdate.internetMessageIds.includes(internetMessageId)
                ) {
                    return null;
                }

                logger.info(
                    [LOGS.CustomViewManager],
                    `_removeInSingleCustomViewIfNeeded - removing ${internetMessageId} from CustomView(${viewToUpdate.name})`,
                );

                // Un mail supprimé alors qu'il attendait son insertion ne sera pas inséré.
                const nextPendingMails =
                    pendingMails ?? new Map<string, PendingMailEntry>();
                nextPendingMails.set(internetMessageId, PENDING_REMOVAL);
                this._pendingMailsByViewId.set(viewId, nextPendingMails);

                // L'écriture n'est PAS attendue sous le verrou : le flush prend ce même verrou,
                // l'attendre ici serait un interblocage. Elle l'est à la sortie.
                return { flush: this._scheduleCustomViewFlush(viewId) };
            },
        );

        await scheduled?.flush;
    }

    /**
     * Vérifie si un mail correspond aux filtres d'une CustomView
     *
     * @param mail - Le mail à valider
     * @param customView - Objet customView contenant les filtres
     * @returns true si le mail satisfait tous les filtres actifs (logique AND)
     */
    private _isMailMatchCustomView(
        mail: Mail,
        customView: CustomView,
    ): boolean {
        // Mails in Deleted Items must never appear in any custom view regardless of filters.
        const deletedItemsFolderId = this.mailSync._getDeletedItemsFolderId();
        if (
            deletedItemsFolderId &&
            mail.parentFolderId === deletedItemsFolderId
        ) {
            return false;
        }

        const { filters } = customView;

        // ===== FILTRAGE PAR MOTS-CLÉS =====
        // Recherche exacte (case insensitive) dans subject, message et uniqueBody
        // TOUS les mots-clés doivent être trouvés (logique AND)
        if (filters.keywords?.length) {
            const mailDataSearch: DataSearchResult = {
                internetMessageId: mail.internetMessageId,
                uniqueBodyContent: mail.uniqueBody,
                subject: mail.subject,
            };

            const isMailMatchingKeywords = isSearchResultMatchKeyWords(
                mailDataSearch,
                filters.keywords,
            );
            return isMailMatchingKeywords;
        }

        // ===== FILTRAGE PAR PLAGE DE DATES =====
        // Utilise sentDateTime du mail et compare avec startDate/endDate (format ISO)
        if (filters.startDate) {
            const mailDate = new Date(mail.sentDateTime as string);
            const startDate = new Date(filters.startDate);

            if (mailDate < startDate) {
                return false;
            }
        }

        if (filters.endDate) {
            const mailDate = new Date(mail.sentDateTime as string);
            const endDate = new Date(filters.endDate);

            if (mailDate > endDate) {
                return false;
            }
        }

        // ===== FILTRAGE PAR EXPÉDITEURS =====
        // Au moins 1 expéditeur du filtre doit correspondre à mail.from
        if (filters.identifiedSenders?.length) {
            const hasMatchingSender = filters.identifiedSenders.some(
                (sender) =>
                    mail.from.email.toLowerCase() === sender.toLowerCase(),
            );

            if (!hasMatchingSender) {
                return false;
            }
        }

        // ===== FILTRAGE PAR DESTINATAIRES =====
        // Au moins 1 destinataire du filtre doit être trouvé dans to, cc ou bcc
        if (filters.identifiedRecipients?.length) {
            const allRecipients = [...mail.to, ...mail.cc, ...mail.bcc];
            const hasMatchingRecipient = filters.identifiedRecipients.some(
                (recipient) =>
                    allRecipients.some(
                        (r) =>
                            r.email.toLowerCase() === recipient.toLowerCase(),
                    ),
            );

            if (!hasMatchingRecipient) {
                return false;
            }
        }

        // ===== FILTRAGE PAR CATÉGORIES =====
        // Utilise les catégories générées par l'IA (aiDetails.categories)
        if (filters.categories?.length) {
            if (!mail.aiDetails?.categories?.length) {
                return false;
            }

            const hasAllCategories = filters.categories.every((category) =>
                mail.aiDetails!.categories.some(
                    (c) => c.toLowerCase() === category.toLowerCase(),
                ),
            );

            if (!hasAllCategories) {
                return false;
            }
        }

        // ===== FILTRAGE PAR STATUT DE LECTURE =====
        if (filters.readStatus) {
            if (filters.readStatus === "read" && !mail.isRead) {
                return false;
            }

            if (filters.readStatus === "unread" && mail.isRead) {
                return false;
            }
        }

        // ===== FILTRAGE PAR PIÈCES JOINTES =====
        if (filters.hasAttachment !== undefined) {
            if (filters.hasAttachment !== mail.hasAttachment) {
                return false;
            }
        }

        // ===== FILTRAGE PAR STATUTS MÉTIER =====
        if (filters.mailStatus?.length) {
            const statusMatches = filters.mailStatus.every((status) => {
                switch (status) {
                    case ViewFilterMailStatus.userProcessed:
                        return mail.isUserProcessed;
                    case ViewFilterMailStatus.notUserProcessed:
                        return !mail.isUserProcessed;
                    case ViewFilterMailStatus.focus:
                        return mail.isFocus;
                    case ViewFilterMailStatus.notFocus:
                        return !mail.isFocus;
                    default:
                        return false;
                }
            });
            if (!statusMatches) return false;
        }

        return true;
    }

    private _doArraysHaveSameElements(arr1: string[], arr2: string[]): boolean {
        if (arr1.length !== arr2.length) {
            return false;
        }

        const set1 = new Set(arr1);
        const set2 = new Set(arr2);

        if (set1.size !== set2.size) {
            return false; // Il y a des doublons différents
        }

        for (const item of set1) {
            if (!set2.has(item)) {
                return false;
            }
        }

        return true;
    }
}
